import React from "react";
import { Paper, Typography, Box, useTheme } from "@mui/material";
import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import moment from "moment";
import CustomEmptyState from "global/components/CustomEmptyState/CustomEmptyState";
import ChartDataTable from "global/components/ChartDataTable/ChartDataTable";

interface Props {
  data: any[];
  isMobile: boolean;
  selectedMetrics: string[];
  /** Omit outer Paper/title; use inside a parent card (e.g. Insights). */
  embedded?: boolean;
  /**
   * Plot height in px, overriding the breakpoint default. `Reports - Mobile &
   * Tablet.dc.html` draws a much shorter chart on a phone than on the tablet,
   * and `isMobile` alone cannot tell those two apart.
   */
  chartHeight?: number;
}

const METRIC_ORDER = ["Requested", "Sent", "Opened", "Completed"];

const EnvelopeProgressGraph = ({
  data,
  isMobile,
  selectedMetrics,
  embedded = false,
  chartHeight,
}: Props) => {
  const theme = useTheme();
  const { series } = theme.palette.chart;
  const embeddedHeight = chartHeight ?? (isMobile ? 320 : 400);

  const LEGEND_COLORS: Record<string, string> = {
    Requested: series.requested,
    Sent: series.sent,
    Opened: series.opened,
    Completed: series.completed,
  };

  const isVisible = (metricName: string) =>
    selectedMetrics.includes(metricName.toLowerCase());

  const maxVisibleTicks = isMobile ? 6 : 10;
  const dynamicTickInterval =
    data.length > maxVisibleTicks
      ? Math.ceil(data.length / maxVisibleTicks) - 1
      : 0;

  const renderCustomLegend = (props: any) => {
    const payload = props?.payload || [];
    if (!payload.length) return null;

    return (
      <Box
        sx={{
          mt: 2.5,
          pb: 1,
          px: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexWrap: "wrap",
          columnGap: isMobile ? 2 : 3,
          rowGap: 1,
        }}
      >
        {payload.map((entry: any) => (
          <Box
            key={entry.value}
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "3px",
                bgcolor:
                  LEGEND_COLORS[entry.value] || entry.color || series.requested,
              }}
            />
            <Typography
              sx={{
                fontSize: "11px",
                color: "text.secondary",
                fontWeight: 600,
              }}
            >
              {entry.value}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  const showYear = React.useMemo(() => {
    if (!data || data.length < 2) return false;
    const firstDate = moment(data[0].originalDate, "MM/DD/YYYY");
    const lastDate = moment(data[data.length - 1].originalDate, "MM/DD/YYYY");
    return !firstDate.isSame(lastDate, "year");
  }, [data]);

  const formatAxisDate = (value: any) =>
    moment(value, "MM/DD/YYYY").format(showYear ? "D MMM YYYY" : "D MMM");

  const seriesInView = METRIC_ORDER.filter(isVisible);

  /**
   * The plot is one graphic to assistive tech (`role="img"` below), so its
   * label has to carry what the axes and legend say visually; the per-date
   * numbers live in the adjacent data table (WCAG 1.1.1).
   */
  const chartSummary = data.length
    ? `Line chart of ${seriesInView.join(", ")} document counts by date, ` +
      `${formatAxisDate(data[0]?.originalDate)} to ` +
      `${formatAxisDate(data[data.length - 1]?.originalDate)}. ` +
      `Every value is listed in the data table that follows.`
    : "Line chart of document counts by date.";

  const tableRows = data.map((entry) => [
    formatAxisDate(entry.originalDate),
    ...seriesInView.map((metric) =>
      Number(entry[metric] ?? 0).toLocaleString(),
    ),
  ]);

  const chart = (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart
        accessibilityLayer={false}
        data={data}
        margin={{
          top: 10,
          right: isMobile ? 10 : 20,
          bottom: isMobile ? 10 : 0,
        }}
      >
        <defs>
          <linearGradient id="sentArea" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={series.sent} stopOpacity={0.14} />
            <stop offset="100%" stopColor={series.sent} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid
          strokeDasharray="4 5"
          vertical={false}
          stroke={theme.palette.chart.grid}
        />
        <XAxis
          dataKey="originalDate"
          axisLine={false}
          tickLine={false}
          tick={{
            fontSize: isMobile ? 10 : 12,
            fill: theme.palette.chart.axis,
          }}
          tickFormatter={formatAxisDate}
          dy={10}
          angle={isMobile ? -45 : 0}
          textAnchor={isMobile ? "end" : "middle"}
          height={isMobile ? 60 : 30}
          minTickGap={isMobile ? 15 : 5}
          interval={dynamicTickInterval}
        />
        <YAxis
          axisLine={false}
          tickLine={false}
          tick={{
            fontSize: isMobile ? 10 : 12,
            fill: theme.palette.chart.axis,
          }}
          width={isMobile ? 35 : 60}
        />
        <Tooltip
          itemSorter={(item) => {
            const index = METRIC_ORDER.indexOf(String(item.name));
            return index !== -1 ? index : 99;
          }}
          labelFormatter={(label) => {
            if (typeof label !== "string") return "";
            return `Date: ${moment(label, "MM/DD/YYYY").format(
              showYear ? "D MMM YYYY" : "D MMM",
            )}`;
          }}
          formatter={(value: any, name: any) => [value ?? 0, String(name)]}
          cursor={{
            stroke: theme.palette.chart.cursor,
            strokeDasharray: "4 4",
          }}
          contentStyle={{
            borderRadius: "8px",
            border: "none",
            boxShadow: theme.palette.chart.tooltipShadow,
            fontSize: isMobile ? "12px" : "14px",
          }}
        />
        <Legend iconType="square" content={renderCustomLegend} />

        {isVisible("requested") && (
          <Line
            type="monotone"
            dataKey="Requested"
            stroke={series.requested}
            strokeWidth={2.6}
            dot={false}
            activeDot={{ r: 6 }}
          />
        )}

        {isVisible("sent") && (
          <Area
            type="monotone"
            dataKey="Sent"
            stroke={series.sent}
            strokeWidth={2.6}
            fill="url(#sentArea)"
            dot={false}
            activeDot={{ r: 6 }}
          />
        )}

        {isVisible("opened") && (
          <Line
            type="monotone"
            dataKey="Opened"
            stroke={series.opened}
            strokeWidth={2.6}
            dot={false}
            activeDot={{ r: 6 }}
          />
        )}

        {isVisible("completed") && (
          <Line
            type="monotone"
            dataKey="Completed"
            stroke={series.completed}
            strokeWidth={2.6}
            dot={false}
            activeDot={{ r: 6 }}
          />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );

  const isAllUnchecked = selectedMetrics.length === 0;
  const hasData = React.useMemo(() => {
    return data.some(
      (d) => d.Requested > 0 || d.Sent > 0 || d.Opened > 0 || d.Completed > 0,
    );
  }, [data]);

  const renderEmptyState = (title: string, description: string) => {
    const emptyState = (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          height: "100%",
        }}
      >
        <CustomEmptyState
          icon="report"
          title={title}
          description={description}
          isMobile={isMobile}
        />
      </Box>
    );

    if (embedded) {
      return (
        <Box
          sx={{
            width: "100%",
            height: embeddedHeight,
            minHeight: embeddedHeight,
            minWidth: 0,
          }}
        >
          {emptyState}
        </Box>
      );
    }

    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          border: "1px solid",
          borderColor: "chart.panelBorder",
          borderRadius: 2,
          height: isMobile ? 400 : 500,
          bgcolor: "chart.canvas",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          justifyContent: "center",
        }}
      >
        {emptyState}
      </Paper>
    );
  };

  if (isAllUnchecked) {
    return renderEmptyState(
      "No status selected",
      "Please select at least one status.",
    );
  }

  if (!hasData) {
    return renderEmptyState(
      "No data available",
      "No data available for the selected date range.",
    );
  }

  const dataTable = (
    <ChartDataTable
      caption="Workflow trends — document counts by date"
      columns={["Date", ...seriesInView]}
      rows={tableRows}
    />
  );

  if (embedded) {
    return (
      <>
        <Box
          role="img"
          aria-label={chartSummary}
          sx={{
            width: "100%",
            height: embeddedHeight,
            minHeight: embeddedHeight,
            minWidth: 0,
          }}
        >
          {chart}
        </Box>
        {dataTable}
      </>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        border: "1px solid",
        borderColor: "chart.panelBorder",
        borderRadius: 2,
        height: isMobile ? 400 : 500,
        bgcolor: "chart.canvas",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mb: 2, ml: 4 }}
      >
        Documents
      </Typography>
      <Box
        role="img"
        aria-label={chartSummary}
        sx={{
          width: "100%",
          height: "100%",
          flexGrow: 1,
          minHeight: 0,
          minWidth: 0,
        }}
      >
        {chart}
      </Box>
      {dataTable}
    </Paper>
  );
};

export default EnvelopeProgressGraph;
